import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import "../login.css";
import {
  ArrowRight,
  Eye,
  EyeSlash,
  LockKey,
  ShieldCheck,
  UserCircle
} from "@phosphor-icons/react";
import { activeScopeTheme, runtimeScope, scopeDirectory } from "../data";

// 登录页原来也挂了 three.js 星流场景 + 一张 CG 大图；三国主题下改成一页宣纸，
// 顺带把那块 1.1MB 的 3D 包从产物里去掉。

export function isWindLoginSurface() {
  const route = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  const { pathname } = window.location;
  if (route === "login") return true;
  const pathMatch = pathname.match(/\/qunxing\/([^/]+)\/login\/?/);
  if (pathMatch) return pathMatch[1] === "login" || pathMatch[1] in scopeDirectory;
  // 统一入口：/qunxing/login/ 与 /qunxing/<分组>/login/ 都算登录面
  return /\/login\/?$/.test(pathname) || /\/login\//.test(pathname);
}

function safeDestination() {
  const requested = new URLSearchParams(window.location.search).get("next");
  if (requested && !requested.startsWith("//") && requested.startsWith("/qunxing/")) return requested;
  return "/qunxing/admin/";
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  return reduced;
}

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const reducedMotion = useReducedMotion();
  const destination = useMemo(safeDestination, []);

  useEffect(() => {
    // 登录页是统一入口：先清掉上一次的分组与联盟归属，登录后再按账号自动定位
    try { window.localStorage.removeItem("alliance:scope"); window.localStorage.removeItem("alliance:slug"); window.localStorage.removeItem("alliance:channel"); } catch { /* 隐私模式下忽略 */ }
    document.documentElement.dataset.scope = runtimeScope;
    document.title = "联盟管理中心登录";
    return () => { delete document.documentElement.dataset.scope; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/wuying/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ user: username, password })
      });
      if (response.ok) {
        window.location.assign(destination);
        return;
      }
      const payload = await response.json().catch(() => ({}));
      setError(typeof payload.error === "string" ? payload.error : "账号或密码不正确");
    } catch {
      setError("认证服务暂时无法连接，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={`wind-login tone-${activeScopeTheme.tone}`} data-scope={runtimeScope}>
      <section className="wind-login-brand" aria-labelledby="wind-login-title">
        <div className="wind-login-lockup">
          <span className="wind-login-seal" aria-hidden="true">盟</span>
          <span><small>ALLIANCE HUB</small><strong>联盟管理中心</strong></span>
        </div>
        <p className="wind-login-kicker">联盟运营管理中心</p>
        <h1 id="wind-login-title">{activeScopeTheme.heroTitle}</h1>
        <p className="wind-login-intro">统一认证后进入{(scopeDirectory[runtimeScope] || scopeDirectory.alliance).label}专属管理端。活动、财务与攻略由授权账号维护，成员与排行保持采集只读。</p>
        <div className="wind-login-trust"><ShieldCheck weight="duotone" /><span><b>范围隔离已启用</b><small>仅加载{(scopeDirectory[runtimeScope] || scopeDirectory.alliance).label}管理数据</small></span></div>
      </section>

      <section className="wind-login-card" aria-label={`${(scopeDirectory[runtimeScope] || scopeDirectory.alliance).label}管理端登录`}>
        <header>
          <p>AUTHORIZED ACCESS</p>
          <h2>进入管理端</h2>
          <span>登录成功后返回“{(scopeDirectory[runtimeScope] || scopeDirectory.alliance).label}”</span>
        </header>
        <form onSubmit={submit}>
          <label htmlFor="wind-user">用户名</label>
          <div className="wind-login-field">
            <UserCircle weight="duotone" />
            <input id="wind-user" name="username" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="请输入管理账号" required />
          </div>
          <label htmlFor="wind-password">密码</label>
          <div className="wind-login-field">
            <LockKey weight="duotone" />
            <input id="wind-password" name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="请输入密码" required />
            <button className="wind-password-toggle" type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "隐藏密码" : "显示密码"}>{showPassword ? <EyeSlash /> : <Eye />}</button>
          </div>
          <div className="wind-login-error" role="alert" aria-live="polite">{error}</div>
          <button className="wind-login-submit" type="submit" disabled={submitting}>{submitting ? "正在核验身份" : "登录并进入"}<ArrowRight weight="bold" /></button>
        </form>
        <footer><ShieldCheck weight="fill" />统一认证 · 会话加密 · 权限隔离</footer>
      </section>
    </main>
  );
}
